import React, { useState } from "react";
import { Brain, Maximize2, Users, TrendingUp, TrendingDown } from "lucide-react";

const card  = "rounded-2xl border border-[#00ff90]/20 bg-[#080808] shadow-[0_0_24px_#00ff9010]";
const NEON  = "#00ff90";

function Bar({ label, value, max, color = NEON }) {
  const pct = max > 0 ? Math.round((value / max) * 100) : 0;
  return (
    <div className="flex items-center gap-3">
      <span className="text-xs text-white/40 w-24 truncate shrink-0">{label}</span>
      <div className="flex-1 h-1.5 rounded-full bg-white/5">
        <div className="h-full rounded-full transition-all duration-700" style={{ width: `${pct}%`, background: color }} />
      </div>
      <span className="text-xs font-mono text-white/50 w-8 text-right shrink-0">{value}</span>
    </div>
  );
}

function Sparkline({ values = [], color = NEON, height = 32 }) {
  if (!values.length) return null;
  const max = Math.max(...values);
  const min = Math.min(...values);
  const range = max - min || 1;
  const w = 80; const h = height;
  const pts = values.map((v, i) => {
    const x = (i / (values.length - 1)) * w;
    const y = h - ((v - min) / range) * (h - 4) - 2;
    return `${x},${y}`;
  }).join(" ");
  return (
    <svg width={w} height={h} className="overflow-visible">
      <polyline points={pts} fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" opacity="0.7" />
    </svg>
  );
}

export default function WorkforceWidget({ data = {}, period, loading, onAskKira, onExpand, expanded }) {
  const [tab, setTab] = useState("overview");

  const depts      = data.by_department || [];
  const maxDept    = Math.max(...depts.map(d => d.count), 1);
  const trend      = data.headcount_trend || [];
  const attrition  = data.attrition_by_dept || [];
  const tenure     = data.tenure_distribution || [];

  return (
    <div className={`${card} flex flex-col`}>
      {/* Header */}
      <div className="flex items-center justify-between px-5 py-4 border-b border-white/5">
        <div className="flex items-center gap-2">
          <Users size={14} className="text-[#00ff90]/60" />
          <span className="text-sm font-semibold text-white/80">Workforce</span>
          {data.headcount && (
            <span className="text-xs text-[#00ff90]/50 border border-[#00ff90]/15 px-2 py-0.5 rounded-full ml-1">
              {data.headcount} people
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => onAskKira(data, "Analyse workforce composition, growth trends and attrition. What are the key risks and recommended actions?")}
            className="flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-lg border border-[#00ff90]/20 bg-[#00ff90]/5 text-[#00ff90]/60 hover:bg-[#00ff90]/10 hover:text-[#00ff90] transition-colors">
            <Brain size={11} /> KIRA
          </button>
          {onExpand && (
            <button onClick={onExpand} className="text-white/20 hover:text-white transition-colors">
              <Maximize2 size={13} />
            </button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 px-4 pt-3">
        {["overview", "departments", "attrition", "tenure"].map(t => (
          <button key={t} onClick={() => setTab(t)}
            className={`text-xs px-3 py-1.5 rounded-lg transition-colors capitalize ${
              tab === t ? "bg-[#00ff90]/10 text-[#00ff90] border border-[#00ff90]/20" : "text-white/30 hover:text-white/60"
            }`}>
            {t}
          </button>
        ))}
      </div>

      <div className="p-5 flex-1 space-y-4">
        {loading ? (
          <div className="space-y-3">
            {[1,2,3].map(i => <div key={i} className="h-8 rounded-lg bg-white/5 animate-pulse" />)}
          </div>
        ) : (

          /* OVERVIEW */
          tab === "overview" && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                {[
                  { label: "Total Headcount",  value: data.headcount,          color: NEON,      delta: data.headcount_growth_pct },
                  { label: "New Hires",         value: data.new_hires,          color: "#60a5fa", delta: null },
                  { label: "Attrition Rate",    value: data.attrition_rate ? `${data.attrition_rate}%` : "—", color: data.attrition_rate > 10 ? "#f87171" : "#34d399", delta: data.attrition_delta },
                  { label: "Avg Tenure",        value: data.avg_tenure_years ? `${data.avg_tenure_years}y` : "—", color: "#fbbf24", delta: null },
                ].map(({ label, value, color, delta }) => (
                  <div key={label} className="rounded-xl border border-white/5 bg-white/3 p-3">
                    <p className="text-xs text-white/30 mb-1">{label}</p>
                    <p className="text-xl font-bold font-mono" style={{ color }}>{value ?? "—"}</p>
                    {delta != null && (
                      <p className={`text-xs mt-0.5 ${delta >= 0 ? "text-[#00ff90]/50" : "text-red-400/50"}`}>
                        {delta >= 0 ? "+" : ""}{delta?.toFixed(1)}% vs last period
                      </p>
                    )}
                  </div>
                ))}
              </div>

              {/* Headcount trend sparkline */}
              {trend.length > 0 && (
                <div className="rounded-xl border border-white/5 bg-white/3 p-4">
                  <div className="flex items-center justify-between mb-3">
                    <p className="text-xs text-white/30 uppercase tracking-wider">Headcount Trend</p>
                    <Sparkline values={trend.map(t => t.count)} color={NEON} height={28} />
                  </div>
                  <div className="flex items-end gap-1 h-16">
                    {trend.map((t, i) => {
                      const max = Math.max(...trend.map(x => x.count));
                      const pct = max > 0 ? (t.count / max) * 100 : 0;
                      return (
                        <div key={i} className="flex-1 flex flex-col items-center gap-1">
                          <div className="w-full rounded-sm" style={{ height: `${Math.max(pct, 8)}%`, background: `${NEON}60` }} />
                          <span className="text-[9px] text-white/20">{t.label?.slice(-3)}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Gender / employment type */}
              {(data.gender_split || data.employment_types) && (
                <div className="grid grid-cols-2 gap-3">
                  {data.gender_split && (
                    <div className="rounded-xl border border-white/5 bg-white/3 p-3">
                      <p className="text-xs text-white/30 mb-2 uppercase tracking-wider">Gender Split</p>
                      {Object.entries(data.gender_split).map(([k, v]) => (
                        <Bar key={k} label={k} value={v} max={data.headcount || 1} color={k === "Male" ? "#60a5fa" : "#f472b6"} />
                      ))}
                    </div>
                  )}
                  {data.employment_types && (
                    <div className="rounded-xl border border-white/5 bg-white/3 p-3">
                      <p className="text-xs text-white/30 mb-2 uppercase tracking-wider">Employment Type</p>
                      {Object.entries(data.employment_types).map(([k, v]) => (
                        <Bar key={k} label={k} value={v} max={data.headcount || 1} />
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )
        )}

        {/* DEPARTMENTS */}
        {!loading && tab === "departments" && (
          <div className="space-y-2">
            {depts.length === 0 ? (
              <p className="text-white/20 text-sm italic text-center py-6">No department data.</p>
            ) : depts.map((d, i) => (
              <div key={i} className="rounded-xl border border-white/5 bg-white/3 px-4 py-3">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-white/70 font-medium">{d.name}</span>
                  <div className="flex items-center gap-3">
                    <span className="text-xs text-white/30">{d.count} people</span>
                    {d.growth_pct != null && (
                      <span className={`text-xs font-mono ${d.growth_pct >= 0 ? "text-[#00ff90]/60" : "text-red-400/60"}`}>
                        {d.growth_pct >= 0 ? "+" : ""}{d.growth_pct?.toFixed(1)}%
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex-1 h-1.5 rounded-full bg-white/5">
                  <div className="h-full rounded-full" style={{ width: `${Math.round((d.count/maxDept)*100)}%`, background: NEON, opacity: 0.6 }} />
                </div>
              </div>
            ))}
          </div>
        )}

        {/* ATTRITION */}
        {!loading && tab === "attrition" && (
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3 mb-4">
              <div className="rounded-xl border border-white/5 bg-white/3 p-3 text-center">
                <p className="text-2xl font-bold font-mono" style={{ color: data.attrition_rate > 10 ? "#f87171" : NEON }}>
                  {data.attrition_rate ?? "—"}%
                </p>
                <p className="text-xs text-white/30 mt-1">Overall attrition</p>
              </div>
              <div className="rounded-xl border border-white/5 bg-white/3 p-3 text-center">
                <p className="text-2xl font-bold font-mono text-[#fbbf24]">{data.voluntary_attrition ?? "—"}%</p>
                <p className="text-xs text-white/30 mt-1">Voluntary</p>
              </div>
            </div>
            {attrition.length === 0 ? (
              <p className="text-white/20 text-sm italic text-center py-4">No attrition data by department.</p>
            ) : attrition.map((d, i) => (
              <div key={i} className="flex items-center gap-3">
                <span className="text-xs text-white/40 w-28 truncate shrink-0">{d.dept}</span>
                <div className="flex-1 h-2 rounded-full bg-white/5">
                  <div className="h-full rounded-full" style={{ width: `${Math.min(d.rate * 5, 100)}%`, background: d.rate > 15 ? "#f87171" : d.rate > 8 ? "#fbbf24" : NEON }} />
                </div>
                <span className="text-xs font-mono text-white/50 w-10 text-right">{d.rate}%</span>
              </div>
            ))}
          </div>
        )}

        {/* TENURE */}
        {!loading && tab === "tenure" && (
          <div className="space-y-2">
            {tenure.length === 0 ? (
              <p className="text-white/20 text-sm italic text-center py-6">No tenure data.</p>
            ) : tenure.map((t, i) => (
              <Bar key={i} label={t.band} value={t.count} max={Math.max(...tenure.map(x => x.count), 1)}
                color={i === 0 ? "#f87171" : i === tenure.length - 1 ? NEON : "#60a5fa"} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}